import {useState} from 'react';
import {AnimatePresence} from 'framer-motion';

import type {SanityModuleFeaturedPopupGrid} from '../../types';

import ModuleWrapper from './ModuleWrapper.client';
import Popup from '../ui/Popup.client';
import {Image} from '@shopify/hydrogen';

const SmallCard = ({image, title, excerpt, setIsOpen, i}: any) => {
  return (
    <div
      key={i}
      onClick={() => setIsOpen(i)}
      className="relative z-10 h-full w-full cursor-pointer overflow-hidden rounded-md bg-light-grey"
    >
      <div className="aspect-w-15 aspect-h-16 relative w-full ">
        <Image
          width="inherit"
          height="inherit"
          data={image}
          alt={title}
          className="transition-standard absolute top-0 left-0 h-full w-full object-cover hover:scale-105"
        />
      </div>
      <div className="w-full p-6 text-center">
        <h6 className="richH6 mt-4 font-bold">{title}</h6>
        {excerpt && <p className="richH7 mt-1 font-bold">{excerpt}</p>}
        <div className="absolute top-4 right-4 w-max">
          <div className="mb-1 flex h-7 w-7 items-center justify-center rounded-full bg-orange bg-opacity-30">
            <svg
              width="16"
              height="15"
              viewBox="0 0 16 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.03905 0.398682C7.61271 0.398682 7.26715 0.744243 7.26715 1.17059V6.76739H1.67034C1.244 6.76739 0.898438 7.11295 0.898438 7.5393C0.898438 7.96564 1.244 8.3112 1.67034 8.3112H7.26715V13.908C7.26715 14.3343 7.61271 14.6799 8.03905 14.6799C8.46539 14.6799 8.81096 14.3343 8.81096 13.908V8.3112H14.4078C14.8341 8.3112 15.1797 7.96564 15.1797 7.5393C15.1797 7.11295 14.8341 6.76739 14.4078 6.76739H8.81096V1.17059C8.81096 0.744243 8.46539 0.398682 8.03905 0.398682V0.398682Z"
                fill="#F27021"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeaturedCard = ({image, title, excerpt, setIsOpen}: any) => {
  return (
    <div
      onClick={() => setIsOpen('feature')}
      className="sticky top-space-s z-10 h-min w-full cursor-pointer rounded-md bg-orange px-10 py-12"
    >
      <div className="aspect-w-1 aspect-h-1 relative w-full bg-light-grey">
        <Image
          width="1000"
          height="1000"
          data={image}
          alt={title}
          className="transition-standard absolute top-0 left-0 h-full w-full object-cover hover:scale-105"
        />
      </div>
      <div className="mt-5 h-full w-full items-end text-center">
        <h2 className="richH2 font-bold text-white">{title}</h2>
        {excerpt && (
          <h6 className="richH6 mt-1 font-bold text-white">{excerpt}</h6>
        )}
      </div>
      <div className="absolute top-4 right-4 w-max">
        <div className="mb-1 flex h-7 w-7 items-center justify-center rounded-full bg-white bg-opacity-30">
          <svg
            width="16"
            height="15"
            viewBox="0 0 16 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.03905 0.398682C7.61271 0.398682 7.26715 0.744243 7.26715 1.17059V6.76739H1.67034C1.244 6.76739 0.898438 7.11295 0.898438 7.5393C0.898438 7.96564 1.244 8.3112 1.67034 8.3112H7.26715V13.908C7.26715 14.3343 7.61271 14.6799 8.03905 14.6799C8.46539 14.6799 8.81096 14.3343 8.81096 13.908V8.3112H14.4078C14.8341 8.3112 15.1797 7.96564 15.1797 7.5393C15.1797 7.11295 14.8341 6.76739 14.4078 6.76739H8.81096V1.17059C8.81096 0.744243 8.46539 0.398682 8.03905 0.398682V0.398682Z"
              fill="#fff"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default function FeaturedPopupGridModule({
  module,
}: {
  module: SanityModuleFeaturedPopupGrid;
}) {
  const {config, heading, items, featuredItem}: any = module;
  const [isOpen, setIsOpen] = useState<any>(false);

  return (
    <>
      {/* Grid */}
      <ModuleWrapper {...config}>
        <h1
          className="richH1 col-start-2 col-end-14 px-gutter text-center font-bold"
          data-testid="section-whats-in-the-kit"
        >
          {heading}
        </h1>
        <div className="grid grid-cols-14 gap-y-6 pt-space-s">
          {/* Featured Item */}
          <div className="col-start-2 col-end-14 lg:col-end-8 lg:mr-16 xl:col-start-3">
            <FeaturedCard {...featuredItem} setIsOpen={setIsOpen} />
          </div>
          {/* Other Items */}
          <div className="col-start-2 col-end-14 grid w-full grid-cols-2 gap-6 md:gap-10 lg:col-start-8 xl:col-end-13">
            {items?.map((card: any, i: number) => (
              <SmallCard key={i} i={i} {...card} setIsOpen={setIsOpen} />
            ))}
          </div>
        </div>
      </ModuleWrapper>
      {/* Popup */}
      <AnimatePresence mode="wait">
        {isOpen !== false && (
          <Popup
            key={isOpen}
            setIsOpen={setIsOpen}
            {...items[isOpen]}
            featuredItem={isOpen === 'feature' && featuredItem}
          />
        )}
      </AnimatePresence>
    </>
  );
}
